import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  Button,
  IconButton,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Alert,
  Divider
} from '@mui/material';
import {
  BarChart as BarChartIcon,
  TrendingUp as TrendingUpIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend
} from 'recharts';
import {
  collection, query, where, getDocs, orderBy, limit, Timestamp
} from 'firebase/firestore';
import { db } from '../../../../firebase';
import { Subscriber, SubscriptionTier } from '../index';
import { formatCurrency, formatDate, formatNumber, formatPercentage } from '../../../../utils/formatting';

interface AnalyticsTabProps {
  organizationId: string;
}

// Analytics data interface
interface AnalyticsData {
  subscriberGrowth: {
    labels: string[];
    data: number[];
  };
  revenueGrowth: {
    labels: string[];
    data: number[];
  };
  tierDistribution: {
    name: string;
    value: number;
    color: string;
  }[];
  paymentTypeDistribution: {
    name: string;
    value: number;
  }[];
  retentionRate: number;
  conversionRate: number;
  avgLifetimeValue: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({ organizationId }) => {
  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<string>('30days');
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    subscriberGrowth: { labels: [], data: [] },
    revenueGrowth: { labels: [], data: [] },
    tierDistribution: [],
    paymentTypeDistribution: [
      { name: 'Monthly', value: 0 },
      { name: 'Annual', value: 0 }
    ],
    retentionRate: 0,
    conversionRate: 0,
    avgLifetimeValue: 0
  });
  
  // Fetch analytics data when component mounts or time range changes
  useEffect(() => {
    fetchAnalyticsData();
  }, [organizationId, timeRange]);
  
  // Fetch analytics data from Firestore
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Calculate start date based on selected time range
      const now = new Date();
      let startDate = new Date();
      
      switch (timeRange) {
        case '7days':
          startDate.setDate(now.getDate() - 7);
          break;
        case '30days':
          startDate.setDate(now.getDate() - 30);
          break;
        case '90days':
          startDate.setDate(now.getDate() - 90);
          break;
        case '6months':
          startDate.setMonth(now.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          startDate.setDate(now.getDate() - 30);
      }
      
      // Fetch all subscribers
      const subscribersRef = collection(db, `organizations/${organizationId}/subscribers`);
      const subscribersSnapshot = await getDocs(subscribersRef);
      
      if (subscribersSnapshot.empty) {
        setAnalyticsData({
          subscriberGrowth: { labels: [], data: [] },
          revenueGrowth: { labels: [], data: [] },
          tierDistribution: [],
          paymentTypeDistribution: [
            { name: 'Monthly', value: 0 },
            { name: 'Annual', value: 0 }
          ],
          retentionRate: 0,
          conversionRate: 0,
          avgLifetimeValue: 0
        });
        setLoading(false);
        return;
      }
      
      // Extract subscribers data
      const subscribers: Subscriber[] = subscribersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Subscriber));
      
      // Fetch subscription tiers to get names
      const tiersRef = collection(db, `organizations/${organizationId}/subscriptionTiers`);
      const tiersSnapshot = await getDocs(tiersRef);
      const tiers = tiersSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        color: COLORS[Math.floor(Math.random() * COLORS.length)]
      }));
      
      // Generate tier distribution data
      const tierDistribution = tiers.map(tier => {
        const count = subscribers.filter(sub => sub.tierId === tier.id).length;
        return {
          name: tier.name,
          value: count,
          color: tier.color
        };
      }).filter(tier => tier.value > 0);
      
      // Generate payment type distribution
      const monthlyCount = subscribers.filter(sub => !sub.isAnnual).length;
      const annualCount = subscribers.filter(sub => sub.isAnnual).length;
      
      const paymentTypeDistribution = [
        { name: 'Monthly', value: monthlyCount },
        { name: 'Annual', value: annualCount }
      ];
      
      // Generate subscriber growth data (last 6 periods based on time range)
      const labelFormat = timeRange === '7days' || timeRange === '30days' 
        ? 'short' // Short date format for shorter time ranges
        : 'month'; // Month name for longer ranges
      
      const subscriberGrowth = generateTimeSeriesData(
        subscribers,
        startDate,
        now,
        'subscribedDate',
        labelFormat
      );
      
      // Generate revenue growth data
      // For each time period, calculate the MRR
      const revenueGrowth = generateRevenueData(
        subscribers,
        startDate,
        now,
        labelFormat
      );
      
      // Calculate other metrics
      const retentionRate = calculateRetentionRate(subscribers);
      const conversionRate = 8.3; // Mock data - would need actual visitor/conversion tracking
      const avgLifetimeValue = calculateAverageLTV(subscribers);
      
      // Set analytics data
      setAnalyticsData({
        subscriberGrowth,
        revenueGrowth,
        tierDistribution,
        paymentTypeDistribution,
        retentionRate,
        conversionRate,
        avgLifetimeValue
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics data:', err);
      setError('Failed to load analytics data. Please try again.');
      setLoading(false);
    }
  };
  
  // Generate time series data for either subscribers or revenue
  const generateTimeSeriesData = (
    data: Subscriber[],
    startDate: Date,
    endDate: Date,
    dateField: 'subscribedDate' | 'renewalDate',
    labelFormat: 'short' | 'month'
  ) => {
    const periods = getTimePeriods(startDate, endDate, timeRange);
    
    // Initialize counts for each period
    const counts = Array(periods.labels.length).fill(0);
    
    // Count subscribers for each period
    data.forEach(item => {
      const itemDate = new Date(item[dateField].seconds * 1000);
      
      for (let i = 0; i < periods.ranges.length; i++) {
        const [periodStart, periodEnd] = periods.ranges[i];
        
        if (itemDate >= periodStart && itemDate <= periodEnd) {
          counts[i]++;
          break;
        }
      }
    });
    
    return {
      labels: periods.labels,
      data: counts
    };
  };
  
  // Generate revenue data over time
  const generateRevenueData = (
    subscribers: Subscriber[],
    startDate: Date,
    endDate: Date,
    labelFormat: 'short' | 'month'
  ) => {
    const periods = getTimePeriods(startDate, endDate, timeRange);
    
    // Initialize revenue for each period
    const revenue = Array(periods.labels.length).fill(0);
    
    // Calculate MRR for each period
    periods.ranges.forEach((range, i) => {
      const [periodStart, periodEnd] = range;
      let periodMRR = 0;
      
      // Find subscribers active during this period
      subscribers.forEach(sub => {
        const subscribedDate = new Date(sub.subscribedDate.seconds * 1000);
        const renewalDate = new Date(sub.renewalDate.seconds * 1000);
        
        // If subscribed before or during this period and renewal is after period start
        if (subscribedDate <= periodEnd && renewalDate >= periodStart) {
          // Add to MRR based on subscription type
          if (sub.isAnnual) {
            periodMRR += sub.amount / 12; // Convert annual to monthly
          } else {
            periodMRR += sub.amount;
          }
        }
      });
      
      revenue[i] = parseFloat(periodMRR.toFixed(2));
    });
    
    return {
      labels: periods.labels,
      data: revenue
    };
  };
  
  // Get time periods based on selected range
  const getTimePeriods = (startDate: Date, endDate: Date, timeRange: string) => {
    const ranges: [Date, Date][] = [];
    const labels: string[] = [];
    
    // Determine period size based on time range
    let periodSize: 'day' | 'week' | 'month' = 'day';
    let periodCount = 30;
    
    switch (timeRange) {
      case '7days':
        periodSize = 'day';
        periodCount = 7;
        break;
      case '30days':
        periodSize = 'day';
        periodCount = 6; // We'll use 5-day periods
        break;
      case '90days':
        periodSize = 'week';
        periodCount = 12; // Approx. 7-8 day periods
        break;
      case '6months':
        periodSize = 'month';
        periodCount = 6;
        break;
      case '1year':
        periodSize = 'month';
        periodCount = 12;
        break;
    }
    
    // Generate periods
    const msPerDay = 24 * 60 * 60 * 1000;
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / msPerDay);
    const daysPerPeriod = Math.ceil(totalDays / periodCount);
    
    for (let i = 0; i < periodCount; i++) {
      const periodStart = new Date(startDate);
      periodStart.setDate(startDate.getDate() + (i * daysPerPeriod));
      
      const periodEnd = new Date(periodStart);
      periodEnd.setDate(periodStart.getDate() + daysPerPeriod - 1);
      
      // Ensure last period doesn't exceed end date
      if (periodEnd > endDate) {
        periodEnd.setTime(endDate.getTime());
      }
      
      ranges.push([periodStart, periodEnd]);
      
      // Format label based on period size
      if (periodSize === 'day' || timeRange === '30days') {
        labels.push(formatDate(periodStart));
      } else if (periodSize === 'week') {
        labels.push(`${formatDate(periodStart)} - ${formatDate(periodEnd)}`);
      } else {
        labels.push(periodStart.toLocaleString('default', { month: 'short' }));
      }
    }
    
    return { ranges, labels };
  };
  
  // Format date for labels
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en', {
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Calculate retention rate
  const calculateRetentionRate = (subscribers: Subscriber[]) => {
    // For simplicity, we're calculating a mock retention rate
    // In a real app, this would compare renewals vs cancellations
    const activeSubscribers = subscribers.filter(s => s.paymentStatus === 'active').length;
    const totalSubscribers = subscribers.length;
    
    return totalSubscribers > 0 ? Math.round((activeSubscribers / totalSubscribers) * 100) : 0;
  };
  
  // Calculate average lifetime value
  const calculateAverageLTV = (subscribers: Subscriber[]) => {
    if (subscribers.length === 0) return 0;
    
    let totalMonthlyValue = 0;
    const averageLifetimeMonths = 12; // Assumption: average customer stays 12 months
    
    subscribers.forEach(sub => {
      if (sub.isAnnual) {
        totalMonthlyValue += sub.amount / 12;
      } else {
        totalMonthlyValue += sub.amount;
      }
    });
    
    const averageMonthlyValue = totalMonthlyValue / subscribers.length;
    return parseFloat((averageMonthlyValue * averageLifetimeMonths).toFixed(2));
  };
  
  // Chart data transformation for recharts
  const chartData = {
    subscriberGrowth: analyticsData.subscriberGrowth.labels.map((label, index) => ({
      name: label,
      subscribers: analyticsData.subscriberGrowth.data[index]
    })),
    revenueGrowth: analyticsData.revenueGrowth.labels.map((label, index) => ({
      name: label,
      revenue: analyticsData.revenueGrowth.data[index]
    }))
  };
  
  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Time range selector */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="h3">
          Subscription Analytics
        </Typography>
        
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel id="time-range-label">Time Range</InputLabel>
          <Select
            labelId="time-range-label"
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value as string)}
          >
            <MenuItem value="7days">Last 7 Days</MenuItem>
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="90days">Last 90 Days</MenuItem>
            <MenuItem value="6months">Last 6 Months</MenuItem>
            <MenuItem value="1year">Last Year</MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Key Metrics */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Retention Rate
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.retentionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active subscribers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Conversion Rate
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.conversionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visitors to subscribers
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Average Customer LTV
                  </Typography>
                  <Typography variant="h4" component="div">
                    {formatCurrency(analyticsData.avgLifetimeValue)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Lifetime value per subscriber
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Payment Preference
                  </Typography>
                  <Typography variant="h4" component="div">
                    {analyticsData.paymentTypeDistribution[1].value > 0 
                      ? Math.round((analyticsData.paymentTypeDistribution[1].value / 
                         (analyticsData.paymentTypeDistribution[0].value + analyticsData.paymentTypeDistribution[1].value)) * 100)
                      : 0}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Choose annual billing
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          
          {/* Charts */}
          <Grid container spacing={3}>
            {/* Subscriber Growth Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <TrendingUpIcon sx={{ mr: 1 }} /> Subscriber Growth
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 300 }}>
                  {chartData.subscriberGrowth.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData.subscriberGrowth}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip formatter={(value) => [`${value} subscribers`, 'New subscribers']} />
                        <Legend />
                        <Bar dataKey="subscribers" fill="#8884d8" name="New subscribers" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        No subscriber data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Revenue Growth Chart */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <TimelineIcon sx={{ mr: 1 }} /> Revenue Growth
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 300 }}>
                  {chartData.revenueGrowth.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={chartData.revenueGrowth}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <YAxis />
                        <RechartsTooltip formatter={(value) => [`$${value}`, 'Monthly Revenue']} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="revenue" 
                          stroke="#82ca9d" 
                          name="Monthly Revenue"
                          activeDot={{ r: 8 }} 
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        No revenue data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Distribution Charts */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ mr: 1 }} /> Subscription Tier Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 300 }}>
                  {analyticsData.tierDistribution.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analyticsData.tierDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {analyticsData.tierDistribution.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip formatter={(value) => [`${value} subscribers`, 'Subscribers']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        No tier distribution data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
            
            {/* Payment Type Distribution */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" component="h3" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ mr: 1 }} /> Payment Type Distribution
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ height: 300 }}>
                  {analyticsData.paymentTypeDistribution.some(item => item.value > 0) ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={analyticsData.paymentTypeDistribution}
                          cx="50%"
                          cy="50%"
                          labelLine={true}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          <Cell fill="#0088FE" />
                          <Cell fill="#00C49F" />
                        </Pie>
                        <RechartsTooltip formatter={(value) => [`${value} subscribers`, 'Subscribers']} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1" color="text.secondary">
                        No payment type data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default AnalyticsTab;
